import { useContext, type ReactElement } from 'react'

import { useIsInternalTesting } from '@lib/hooks/use-internal-testing'

import type { TypeAssetVideoEntry } from '@contentful-types/asset-video'

import { VideoType } from '@constants/enums/video'

import { SectionContext } from '@contexts/section-context'

import { updateAssetCDNUrl } from '@components/asset/utils'
import type { Source, VideoProps } from '@components/ui-core/video/video'

import { AnimationVideo } from './animation-video/animation-video'

type AssetVideoProps = {
  className?: string
  data: TypeAssetVideoEntry
}

export function VideoAsset({ data }: AssetVideoProps): ReactElement | null {
  const {
    videoDescription,
    assets,
    aspectRatio,
    videoType,
    poster,
    autoLoop,
    autoPlay,
    internalName,
    controlsLocation,
    isRounded = false,
  } = data.fields
  const { indexInPage } = useContext(SectionContext)
  const isInternalTesting = useIsInternalTesting()

  if (videoType !== VideoType.Animation) {
    console.log(`Video type ${videoType} not currently supported.`)
    // @TODO: Add Wistia functionality https://grammarly.atlassian.net/browse/WBDX-126
    // @TODO: Add YouTube functionality https://grammarly.atlassian.net/browse/WBDX-127
  }

  const videoSources: Source[] =
    assets
      ?.filter(asset => asset?.fields?.file?.contentType?.split('/')?.[0] === 'video')
      ?.map((asset, index) => ({
        id: asset?.sys?.id ?? index,
        type: asset?.fields?.file?.contentType ?? '',
        url: updateAssetCDNUrl(asset?.fields?.file?.url),
      })) ?? []

  const isAnimation = videoType === VideoType.Animation && videoSources.length > 0

  if (!isAnimation) {
    return null
  }

  const [width, height] = aspectRatio?.split(':') ?? [undefined, undefined]

  const videoOptions: VideoProps = {
    autoPlay: autoPlay && !isInternalTesting,
    loop: autoLoop,
    sources: videoSources,
    controls: false,
    poster: updateAssetCDNUrl(poster?.fields.file?.url),
    lazyLoad: !!(indexInPage && indexInPage > 1),
    width,
    height,
  }

  return (
    <div data-component-id={internalName}>
      <AnimationVideo
        controlsLocation={controlsLocation}
        isRounded={isRounded}
        videoOptions={videoOptions}
      />
      {videoDescription && <div className="visually-hidden">{videoDescription}</div>}
    </div>
  )
}
